<template>
	<div :class="classes">
		<div class="segment--left" v-if="$slots['left-icon']">
			<slot name="left-icon"></slot>
		</div>
		<div class="segment--mid">
			<slot></slot>
		</div>
		<div class="segment--right" v-if="$slots['right-icon']">
			<slot name="right-icon"></slot>
		</div>
	</div>
</template>

<script>
import { Enum } from "@teaming/common-enums";

export const TEAMING_CHIP2_SIZE = Enum.create( {
	SMALL: "SMALL",
	MEDIUM: "MEDIUM",
} );

const sizeClassMap = {
	[ TEAMING_CHIP2_SIZE.SMALL ]: "small",
	[ TEAMING_CHIP2_SIZE.MEDIUM ]: "medium",
};

export default {
	name: "TeamingChip2",
	components: {},
	props: {
		disabled: {
			type: Boolean,
			default: false,
		},
		selected: {
			type: Boolean,
			default: false,
		},
		focused: {
			type: Boolean,
			default: false,
		},
		size: {
			type: String,
			default: TEAMING_CHIP2_SIZE.MEDIUM,
			validator( value ) {
				return TEAMING_CHIP2_SIZE.includes( value );
			},
		},
	},
	computed: {
		classes() {
			const baseClass = "teaming-chip2";
			const extendedClass = this.selected ?
				`${ baseClass }--selected` :
				`${ baseClass }--default`;
			const sizeClass = sizeClassMap[ this.size ] || "";

			return {
				[ baseClass ]: true,
				[ extendedClass ]: true,
				disabled: this.disabled,
				focused: this.focused,
				[ sizeClass ]: !!sizeClass,
			};
		},
	},
};
</script>

<style lang="scss" scoped>
$enabledColor--default: $color1-700;
$enabledBackgroundColor--default: $white;
$enabledBorderColor--default: $color1-400;
$enabledIconColor--default: $color1-500;

$hoveredColor--default: $color1-700;
$hoveredBackgroundColor--default: $color1-050;
$hoveredBorderColor--default: $color1-400;
$hoveredIconColor--default: $color1-500;

$focusedColor--default: $color1-700;
$focusedBackgroundColor--default: $color1-100;
$focusedBorderColor--default: $color1-500;
$focusedIconColor--default: $color1-500;

$disabledColor--default: $color1-200;
$disabledBackgroundColor--default: $white;
$disabledBorderColor--default: $color1-100;
$disabledIconColor--default: $color1-200;

$enabledColor--selected: $color2-700;
$enabledBackgroundColor--selected: $color2-100;
$enabledBorderColor--selected: $enabledBackgroundColor--selected;
$enabledIconColor--selected: $color2-500;

$hoveredColor--selected: $color2-700;
$hoveredBackgroundColor--selected: $color2-200;
$hoveredBorderColor--selected: $hoveredBackgroundColor--selected;
$hoveredIconColor--selected: $color2-600;

$focusedColor--selected: $color2-700;
$focusedBackgroundColor--selected: $color2-200;
$focusedBorderColor--selected: $color2-500;
$focusedIconColor--selected: $color2-600;

$disabledColor--selected: $color1-300;
$disabledBackgroundColor--selected: $color1-100;
$disabledBorderColor--selected: $disabledBackgroundColor--selected;
$disabledIconColor--selected: $color1-300;

.teaming-chip2 {
	display: inline-flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	gap: 0.35125rem;

	border-radius: 0.5rem;
	border: 0.0625rem solid transparent;

	font-weight: 600;

	cursor: pointer;
}

// text occupies the most space
.teaming-chip2 .segment--mid {
	flex: 1;
}

// center the icon within the segment
.teaming-chip2 .segment--left,
.teaming-chip2 .segment--right {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
}

.teaming-chip2.medium {
	@include font-and-line-for(0);
	padding: 0.5rem 1rem;

	.segment--left,
	.segment--right {
		@include font-and-line-for(1);
	}
}

.teaming-chip2.small {
	@include font-and-line-for(-1);
	padding: 0.25rem 0.5rem;

	.segment--left,
	.segment--right {
		@include font-and-line-for(0);
	}
}

.teaming-chip2.disabled {
	cursor: not-allowed;
}

.teaming-chip2.teaming-chip2--default {
	color: $enabledColor--default;
	background-color: $enabledBackgroundColor--default;
	border-color: $enabledBorderColor--default;
	svg { color: $enabledIconColor--default; }

	&:hover {
		color: $hoveredColor--default;
		background-color: $hoveredBackgroundColor--default;
		border-color: $hoveredBorderColor--default;
		svg { color: $hoveredIconColor--default; }
	}

	&.focused {
		color: $focusedColor--default;
		background-color: $focusedBackgroundColor--default;
		border-color: $focusedBorderColor--default;
		svg { color: $focusedIconColor--default; }
	}

	&.disabled {
		color: $disabledColor--default;
		background-color: $disabledBackgroundColor--default;
		border-color: $disabledBorderColor--default;
		svg { color: $disabledIconColor--default; }
	}
}

.teaming-chip2.teaming-chip2--selected {
	color: $enabledColor--selected;
	background-color: $enabledBackgroundColor--selected;
	border-color: $enabledBorderColor--selected;
	svg { color: $enabledIconColor--selected; }

	&:hover {
		color: $hoveredColor--selected;
		background-color: $hoveredBackgroundColor--selected;
		border-color: $hoveredBorderColor--selected;
		svg { color: $hoveredIconColor--selected; }
	}

	&.focused {
		color: $focusedColor--selected;
		background-color: $focusedBackgroundColor--selected;
		border-color: $focusedBorderColor--selected;
		svg { color: $focusedIconColor--selected; }
	}

	&.disabled {
		color: $disabledColor--selected;
		background-color: $disabledBackgroundColor--selected;
		border-color: $disabledBorderColor--selected;
		svg { color: $disabledIconColor--selected; }
	}
}
</style>
